import { z } from 'zod';

/** Pacing limits per channel (plan §7). Defaults are conservative on purpose (D-25). */
export const ChannelLimitsSchema = z.object({
  perMinute: z.number().int().min(1).max(30).default(6),
  minGapSec: z.number().int().min(1).max(120).default(8),
  jitterSec: z.number().int().min(0).max(60).default(7),
  perGroupPerHour: z.number().int().min(1).max(200).default(20),
  dailyCap: z.number().int().min(1).max(5000).default(400),
});
export type ChannelLimits = z.infer<typeof ChannelLimitsSchema>;
export const DEFAULT_CHANNEL_LIMITS: ChannelLimits = ChannelLimitsSchema.parse({});

/** Quiet hours: messages are HELD (not dropped) until the window ends. Shabbat/holidays via @hebcal/core. */
export const QuietHoursSchema = z.object({
  enabled: z.boolean().default(true),
  /** "21:00" */
  start: z.string().regex(/^\d{2}:\d{2}$/).default('21:00'),
  /** "08:00" */
  end: z.string().regex(/^\d{2}:\d{2}$/).default('08:00'),
  shabbatAndHolidays: z.boolean().default(true),
  /** if false, critical alerts are also held (default per plan §7) */
  criticalBypass: z.boolean().default(false),
  timezone: z.string().default('Asia/Jerusalem'),
  /** D-170: local weekdays with no sending at all (0 = Sunday … 6 = Saturday) — held until `end` of the next working day */
  offDays: z.array(z.number().int().min(0).max(6)).max(6).optional(),
});
export type QuietHours = z.infer<typeof QuietHoursSchema>;
export const DEFAULT_QUIET_HOURS: QuietHours = QuietHoursSchema.parse({});

export const MESSAGE_KINDS = [
  'text',
  'image',
  'video',
  'audio',
  'voice',
  'document',
  'location',
  'contact',
  'poll',
  'reaction',
  'edit',
  'delete',
] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

/** Kind-specific payload stored on OutboxMessage.payload. */
export const MessagePayloadSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), text: z.string().min(1).max(4096), mentions: z.array(z.string()).optional(), quotedProviderMessageId: z.string().optional() }),
  z.object({
    kind: z.enum(['image', 'video', 'audio', 'voice', 'document']),
    /** MediaObject id (R2) or an https URL (Public API) */
    mediaId: z.string().optional(),
    url: z.string().url().optional(),
    mimeType: z.string().optional(),
    filename: z.string().optional(),
    caption: z.string().max(1024).optional(),
    quotedProviderMessageId: z.string().optional(),
  }),
  z.object({ kind: z.literal('location'), latitude: z.number(), longitude: z.number(), name: z.string().optional(), address: z.string().optional() }),
  z.object({ kind: z.literal('contact'), name: z.string(), phone: z.string() }),
  z.object({ kind: z.literal('poll'), question: z.string().min(1), options: z.array(z.string().min(1)).min(2).max(12), multiple: z.boolean().default(false) }),
  z.object({ kind: z.literal('reaction'), targetProviderMessageId: z.string(), emoji: z.string() }),
  z.object({ kind: z.literal('edit'), targetProviderMessageId: z.string(), text: z.string().min(1) }),
  z.object({ kind: z.literal('delete'), targetProviderMessageId: z.string(), forEveryone: z.boolean().default(true) }),
]);
export type MessagePayload = z.infer<typeof MessagePayloadSchema>;

export const OUTBOX_STATUSES = [
  'queued',
  'held',
  'sending',
  'sent',
  'delivered',
  'read',
  'failed',
  'blocked',
  'cancelled',
  'unknown',
] as const;
export type OutboxStatus = (typeof OUTBOX_STATUSES)[number];

export const OUTBOX_STATUS_LABELS_HE: Record<OutboxStatus, string> = {
  queued: 'בתור',
  held: 'ממתינה לשעות פעילות',
  sending: 'בשליחה',
  sent: 'נשלחה',
  delivered: 'נמסרה',
  read: 'נקראה',
  failed: 'נכשלה',
  blocked: 'נחסמה',
  cancelled: 'בוטלה',
  unknown: 'לא ידוע',
};

/** Why the Outbox refused / held a message. Stored in OutboxMessage.blockedReason. */
export const BLOCK_REASONS = [
  'kill_switch_global',
  'kill_switch_channel',
  'kill_switch_client',
  'kill_switch_site',
  'client_draft',
  'client_paused',
  'staging_allowlist',
  'test_only_until_active', // production: only the internal test group until the client is activated (ADR-0007)
  'channel_unavailable',
  'contact_blocked',
  'private_not_allowed',
  'daily_cap',
] as const;
export type BlockReason = (typeof BLOCK_REASONS)[number];
